import Plan from 'types/Plan';
import Logo from '@alltrails/shared/denali/components/Logo';
import Texture from '@alltrails/shared/denali/components/Texture';
import Typography from '@alltrails/shared/denali/components/Typography';
import { MEDIA_QUERY_SMALL_DOWN } from 'utils/constants/breakpoints';
import { UserId } from '@alltrails/shared/types/User';
import PrimaryLink from 'components/LandingPages/PrimaryLink';
import NoSsr from '@material-ui/core/NoSsr';
import { lazy, Suspense } from 'react';
import chevronDownBouncing from 'components/shared/ChevronDownBouncing/chevron-down-bouncing.svg';
import ProFeatureComparisonTable from 'components/ProFeatureComparisonTable';
import useUser from 'hooks/useUser';
import usePlusFeatures from 'components/LandingPages/hooks/usePlusFeatures';
import Link from '@alltrails/shared/denali/components/Link';
import heroDesktopJpg from './images/hero-2880w.jpg';
import heroDesktopWebp from './images/hero-2880w.webp';
import heroDesktopAvif from './images/hero-2880w.avif';
import heroMobileJpg from './images/hero-750w.jpg';
import heroMobileWebp from './images/hero-750w.webp';
import heroMobileAvif from './images/hero-750w.avif';
import * as styles from './MerchLandingPage.module.scss';

// Desktop: https://www.figma.com/file/vRtgvTRgNNW7cXrqHsFxZV/F_2023_Gear-Drops?type=design&node-id=1565-28700&t=9ose3mXEsqNnAVTD-0
// Mobile: https://www.figma.com/file/vRtgvTRgNNW7cXrqHsFxZV/F_2023_Gear-Drops?type=design&node-id=1565-28802&t=9ose3mXEsqNnAVTD-0

const ChevronDownBouncing = lazy(() => import('components/shared/ChevronDownBouncing'));

type MerchLandingPageProps = {
  planTypes: Plan[];
  userId?: UserId;
};

export default function MerchLandingPage({ planTypes, userId }: MerchLandingPageProps) {
  const user = useUser();
  const plan = planTypes[0];
  const params = new URLSearchParams(__AT_DATA__.search);
  if (userId) {
    params.set('uid', String(userId));
  }
  const search = params.toString();
  const to = user || userId ? `/plus/checkout${search.length > 0 ? `?${search}` : ''}` : `/signup${search.length > 0 ? `?${search}` : ''}`;
  const features = usePlusFeatures();
  const title = (
    <>
      <Typography component="div">
        <p>Tools for a year +</p>
      </Typography>

      <Typography component="div">
        <p>$20 off gear</p>
      </Typography>
    </>
  );

  const disclaimer = <p>*We&apos;ll email your code after any free trial period. Offer ends Monday, June 12th.</p>;

  const subhead = (
    <div>
      <Typography className={styles.stepsHeader} component="div">
        <p>
          AllTrails+ gives you tools to get outside faster, easier, and with more confidence. From now through June 12, it gives you $20 off in the
          AllTrails Gear Shop, too!
        </p>
      </Typography>

      <div className={styles.steps}>
        <Typography className={styles.step} component="div">
          <p>Step 1: Upgrade to AllTrails+ today for just $35.99.</p>
        </Typography>
        <Typography className={styles.step} component="div">
          <p>Step 2: Check email for your $20-off Gear Shop code.*</p>
        </Typography>
        <Typography className={styles.step} component="div">
          <span>Step 3: You’re set! Go grab some </span>
          <Link className={styles.link} target="_blank" href="https://shop.alltrails.com">
            <span>swag</span>
          </Link>
          <span>.</span>
        </Typography>
      </div>
    </div>
  );

  return (
    <>
      <div className={styles.wrapper}>
        <div className={styles.hero}>
          <picture className={styles.picture}>
            <source media={MEDIA_QUERY_SMALL_DOWN} srcSet={heroMobileAvif} type="image/avif" />
            <source media={MEDIA_QUERY_SMALL_DOWN} srcSet={heroMobileWebp} type="image/webp" />
            <source media={MEDIA_QUERY_SMALL_DOWN} srcSet={heroMobileJpg} type="image/jpg" />
            <source srcSet={heroDesktopAvif} type="image/avif" />
            <source srcSet={heroDesktopWebp} type="image/webp" />
            <img alt="People holding merch" src={heroDesktopJpg} />
          </picture>
        </div>
        <div className={styles.content}>
          <div className={styles.copy}>
            <div className={styles.logoDesktop}>
              <Logo size="sm" color="light" variant="plus-logomark" />
            </div>
            <div className={styles.logoMobile}>
              <Logo size="sm" color="dark" variant="plus-logomark" />
            </div>
            <div className={styles.title}>{title}</div>
            <div className={styles.subhead}>{subhead}</div>
            <div className={styles.button}>
              <PrimaryLink buttonText="Get AllTrails+" plan={plan} coupon={null} to={to} desktopVariant="elevated" mobileVariant="primary" />
              <Typography className={styles.cancel} component="div" mb="8" variant="text100">
                <p>Cancel anytime.</p>
              </Typography>
              <Typography className={styles.mobileDisclaimer} variant="text100">
                {disclaimer}
              </Typography>
            </div>
            <Typography className={styles.lottie} component="div" mb="8">
              <NoSsr>
                <Suspense fallback={<img alt="" src={chevronDownBouncing} />}>
                  <ChevronDownBouncing />
                </Suspense>
              </NoSsr>
            </Typography>
            <Typography className={styles.desktopDisclaimer} variant="text100">
              {disclaimer}
            </Typography>
          </div>
          <ProFeatureComparisonTable features={features} containerStyles={styles.features} />
        </div>
      </div>
      <Texture variant="sunflower" className={styles.texture} />
    </>
  );
}
